import React, { useContext, useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { ContentfulContext } from '../../../Context/Contentful';
import { CartContext } from '../../../Context/CartContext';
import { UserContext } from '../../../Context/User';
import { EmotionBreakpoint } from 'dw-au-react-components';
import { Loader } from '../../Loader';
import CtaCartIcon from '../../../svg/CtaCartIcon';
import { useBrandDetails } from 'dw-au-react-components';
import Modal from '../../Modal/Modal';
import axios from 'axios';
import RecruitmentCasesModal from './RecruitmentCasesModal';
import Awards from './Awards';

const StyledRecruitmentCases = styled.div`
	h4 {
		font-size: ${(props) => (props.titleFontSize || '32') + 'px'};
		line-height: ${(props) => (props.titleLineHeight || '40') + 'px'};
		letter-spacing: 0.25px;
		color: ${(props) => props.titleColor || '#212121'};
	}

	p {
		font-size: 16px;
		line-height: 24px;
		letter-spacing: 0.15px;
		color: ${(props) => props.copyColor || '#545454'};
	}
`;

const StyledRecruitmentCasesFilter = styled.div`
	button {
		font-weight: normal;
		font-size: 14px;
		line-height: 20px;
		margin: 0 10px 10px;
	}
`;

const StyledRecruitmentProduct = styled.div`
	background: #ffffff;
	border: 1px solid #e3e3e3;
	border-radius: 8px;

	${EmotionBreakpoint('notMobile')} {
		padding: 40px;
	}

	${EmotionBreakpoint('mobile')} {
		padding: 16px;
	}

	.image {

		${EmotionBreakpoint('notMobile')} {
			margin-right: 20px;

			img {
				width: 254px;
			}
		}

		${EmotionBreakpoint('mobile')} {
			margin: 0 0 16px;

			img {
				width: 100%;
			}
		}
	}

	h6 {
		font-size: 20px;
		line-height: 28px;
		letter-spacing: 0.15px;
		color: #212121;
	}

    .sku {

        ${EmotionBreakpoint('notMobile')} {
            width: 220px;
            padding-left: 40px;
            margin-left: 40px;
            border-left: 1px solid #E3E3E3;
        }
    }

    .price {
        color #117B53;

		&.adc {
			color: #002F49;
		}

		&.newstf {
			color: #002F49;
		}

		&.vir {
			color: #c00;
		}

        span {

            &:first-of-type {
              font-size: 24px;
              line-height: 32px;
            }
          }
    }

    .view {
        text-decoration: underline;
    }
`;

function RecruitmentCases({
	props: {
		title,
		titleColor,
		titleAlignment,
		titleFontSize,
		titleLineHeight,
		copy,
		copyColor,
		copyAlignment,
		showTerms,
		showBottlePrice,
		winePlanFilter,
		totalCopy,
		ctaCopy,
		customFilterOptions = {},
		showFilterBannerImage = false,
	},
}) {
	const { tag, country } = useBrandDetails();
	const {
		products: mainItems,
		getCaseContents,
		preloadCaseContents,
		toggleModal,
		caseItems,
		showModal,
		modalLoading,
		modalItemCode,
		allowExistingCustomer,
	} = useContext(ContentfulContext);
	const { userData } = useContext(UserContext);
	const { addtoCart, setStep } = useContext(CartContext);
	const customFilter =
		customFilterOptions?.items && customFilterOptions?.items.length > 0;
	const [activeTab, setActiveTab] = useState(
		customFilter
			? customFilterOptions?.items[0]?.link
			: country === 'NZ'
			? 'choice red'
			: tag === 'vir'
			? 'expert'
			: ''
	);
	let isLoggedIn = userData && userData.securityStatus >= 4;

	let filters = [
		{
			title: 'Most Popular',
			keyword: '',
		},
		{
			title: 'Discovery Club',
			keyword: 'discovery',
		},
		{
			title: "Experts' Choice Plus",
			keyword: 'experts',
		},
		{
			title: 'European Reds',
			keyword: 'european',
		},
		{
			title: 'Big Reds',
			keyword: 'big reds',
		},
		{
			title: "Connoisseur's Series",
			keyword: 'connoisseur',
		},
	];

	if (tag === 'vir') {
		filters = [
			{
				title: "Experts' Choice Plus",
				keyword: 'expert',
			},
			{
				title: 'Discovery Club',
				keyword: 'discovery',
			},
			{
				title: 'European Reds',
				keyword: 'european',
			},
			{
				title: 'Big Reds',
				keyword: 'big reds',
			},
			{
				title: "Connoisseur's Series",
				keyword: 'connoisseur',
			},
		];
	} else if (country === 'NZ' && tag === 'law') {
		filters = [
			{
				title: "Experts' Choice Reds",
				keyword: 'choice red',
			},
			{
				title: "Experts' Choice Mixed",
				keyword: 'choice mixed',
			},
			{
				title: "Experts' Choice Whites",
				keyword: 'choice white',
			},
		];
	}

	if (customFilter) {
		filters = [];
		customFilterOptions?.items.map(({ title, link: keyword }) => {
			filters.push({
				title: title,
				keyword,
			});
		});
	}

	useEffect(() => {
		if (mainItems && mainItems.length > 0) {
			mainItems.forEach((item) => {
				if (item?.product?.itemCode) {
					preloadCaseContents(item.product.itemCode);
				}
			});
		}
	}, [mainItems, preloadCaseContents]);

	return mainItems && mainItems.length > 0 ? (
		<StyledRecruitmentCases
			titleColor={titleColor}
			copyColor={copyColor}
			titleFontSize={titleFontSize}
			titleLineHeight={titleLineHeight}
		>
			{showFilterBannerImage && customFilter ? (
				<div className='text-center wineplan_filter_images'>
					{customFilterOptions?.items.map(
						({ imagePath, mobileImagePath, title, link }) => {
							return (
								<picture
									key={link}
									className={`mb-40 wineplan_filter_image_${link} ${
										activeTab === link ? 'd-inline-block' : 'd-none'
									}`}
								>
									<source
										media='(max-width: 767px)'
										srcSet={mobileImagePath || imagePath}
									/>
									<img
										src={imagePath}
										alt={title}
										title={title}
										loading='lazy'
										className='mw-100'
									/>
								</picture>
							);
						}
					)}
				</div>
			) : null}
			<div className='container'>
				{title && (
					<h4 className={'lower mb-20 text-' + titleAlignment}>{title}</h4>
				)}
				{copy && <p className={'mb-20 text-' + copyAlignment}>{copy}</p>}

				{winePlanFilter && (
					<StyledRecruitmentCasesFilter className='d-flex justify-content-center align-items-center flex-wrap mb-10'>
						{filters.map((filter, index) => {
							let { title, keyword } = filter;
							return (
								<button
									type='button'
									key={index}
									className={`btn btn-${
										activeTab === keyword ? 'red' : 'secondary'
									}`}
									onClick={() => {
										setActiveTab(keyword);
									}}
								>
									{title}
								</button>
							);
						})}
					</StyledRecruitmentCasesFilter>
				)}

				{mainItems.map((item) => {
					let {
							product: {
								itemCode,
								largeImage,
								name,
								skus: [{ salePrice, salePricePerBottle, numberOfBottles }],
								description,
								webHeadline,
							},
						} = item,
						mysteryCase =
							name.toLowerCase().includes('mystery') ||
							name.toLowerCase().includes('secret')
								? true
								: false;

					const matchedCaseItem = caseItems.find(
						(caseObj) => caseObj.itemCode === itemCode
					);
					const allCaseAccolades =
						matchedCaseItem?.content?.flatMap((contentItem) =>
							contentItem.referencedProduct?.accolades?.length
								? contentItem.referencedProduct.accolades
								: []
						) || [];

					if (
						name.toLowerCase().indexOf('addon') === -1 &&
						name.toLowerCase().indexOf('add-on') === -1 &&
						name.toLowerCase().indexOf('add on') === -1 &&
						(!winePlanFilter ||
							activeTab.toLowerCase() === 'all' ||
							(activeTab && name.toLowerCase().indexOf(activeTab) > -1) ||
							(!activeTab &&
								((name.toLowerCase().indexOf('experts') > -1 &&
									name.toLowerCase().indexOf('choice plus') > -1 &&
									tag !== 'vir') ||
									(country === 'NZ' &&
										name.toLowerCase().indexOf('choice red') > -1))))
					) {
						return (
							<StyledRecruitmentProduct
								key={itemCode}
								className='d-md-flex justify-content-between align-items-center mb-20'
							>
								<div className='d-md-flex justify-content-start align-items-center'>
									<div
										className='image cursor-pointer'
										onClick={() => !mysteryCase && getCaseContents(itemCode)}
									>
										<img
											src={`/images/${
												country?.toLowerCase() || 'au'
											}/en/product/${itemCode}.png`}
											alt={name}
											title={name}
											onError={(e) => {
												if (!e.target.src.includes(largeImage)) {
													e.target.src = largeImage;
												} else {
													e.target.onerror = null;
													e.target.src =
														'/images/au/common/dmlp/default_mixed.jpg';
												}
											}}
											className='d-inline-block'
										/>
									</div>
									<div>
										<h6
											className='mb-12 lower cursor-pointer'
											onClick={() => !mysteryCase && getCaseContents(itemCode)}
										>
											{name}
										</h6>
										<Awards accolades={allCaseAccolades} showViewAll={false} />
										{description ? (
											<div
												className='mb-12'
												onClick={() =>
													!mysteryCase && getCaseContents(itemCode)
												}
											>
												{description}
											</div>
										) : webHeadline ? (
											<div
												className='mb-12'
												onClick={() =>
													!mysteryCase && getCaseContents(itemCode)
												}
											>
												{webHeadline}
											</div>
										) : null}
										{!mysteryCase && (
											<span
												className='mb-12 view d-inline-block cursor-pointer'
												onClick={() => getCaseContents(itemCode)}
											>
												View case details
											</span>
										)}
									</div>
								</div>
								<div className='sku text-left text-md-center flex-shrink-0'>
									{showBottlePrice ? (
										<>
											<div className={'price mb-8 font-weight-bold ' + tag}>
												<span>${salePricePerBottle.toFixed(2)}</span>
												{' / '}
												<span>bottle ({numberOfBottles})</span>
											</div>
											<div className='mb-12'>
												{totalCopy ? totalCopy : 'Total'} $
												{salePrice.toFixed(2)}
											</div>
										</>
									) : (
										<h6 className='mb-12 lower roboto'>
											{totalCopy ? totalCopy : 'Total'} ${salePrice.toFixed(2)}
										</h6>
									)}

									<div>
										<button
											className='btn btn-primary w-100 d-flex justify-content-center align-items-center '
											onClick={() => {
												addtoCart(item);
												setStep(isLoggedIn && allowExistingCustomer ? 0 : 2);
											}}
										>
											<CtaCartIcon />
											<span className='d-inline-block pl-8'>
												{ctaCopy ? ctaCopy : 'Buy now'}
											</span>
										</button>
									</div>
								</div>
							</StyledRecruitmentProduct>
						);
					}
				})}
				{showTerms && (
					<div className='mt-20 text-medium text-center'>
						Read full{' '}
						<a href='/terms' target='_blank' className='text-medium'>
							Terms & Conditions
						</a>
					</div>
				)}
				<Modal
					showModal={showModal}
					modalSize='800px'
					toggleModal={toggleModal}
				>
					{!modalLoading ? (
						<div className=''>
							{caseItems.length > 0 &&
								caseItems.some((item) => item.itemCode === modalItemCode) && (
									<RecruitmentCasesModal
										product={caseItems}
										modalItemCode={modalItemCode}
									/>
								)}
						</div>
					) : (
						<Loader />
					)}
				</Modal>
			</div>
		</StyledRecruitmentCases>
	) : null;
}

export default RecruitmentCases;
